import { useMemo, useState } from 'react';
import { EMPTY, resolveBracket, roundLabel } from '../../../lib/bracket';
import { bracketFor } from '../../../lib/stages';
import { cn } from '../../../lib/utils';

const MATCH_W = 216;
const MATCH_H = 64;
const GAP_X = 48;
const GAP_Y = 20;
const HEAD_H = 28;

// x/y for every match of one side. A match sits level with the average of
// the same-side matches it takes winners from, so trees line up without DOM
// measuring.
const layoutSide = (matches, structure) => {
  const column = (m) => (m.side === 'GF' ? structure.rounds + m.round - 1 : m.round - 1);
  const pos = {};
  const columns = {};
  matches.forEach((m) => {
    const c = column(m);
    columns[c] = columns[c] || [];
    columns[c].push(m);
  });
  Object.keys(columns)
    .map(Number)
    .sort((a, b) => a - b)
    .forEach((c) => {
      columns[c].forEach((m, i) => {
        const feeders = m.sources.filter(
          (s) => s.match && pos[s.match] && (s.take === 'winner' || s.take === 'slot0')
        );
        const y = feeders.length
          ? feeders.reduce((sum, s) => sum + pos[s.match].y, 0) / feeders.length
          : i * (MATCH_H + GAP_Y);
        pos[m.id] = { x: c * (MATCH_W + GAP_X), y, column: c };
      });
    });
  return pos;
};

// "Upper quarterfinals" -> "Upper QF", so drop-in labels fit a match card.
const shortRound = (structure, m) => {
  const label = roundLabel(structure, m.side, m.round)
    .replace(/quarterfinals/i, 'QF')
    .replace(/semifinals/i, 'SF')
    .replace(/round (\d+)/i, 'R$1');
  const many = structure.matches.filter((x) => x.side === m.side && x.round === m.round).length > 1;
  return many ? `${label} ${m.index}` : label;
};

// Name for an undecided slot: lower-bracket slots filled by an upper-bracket
// loser say where that player comes from, everything else is "TBD".
const pendingName = (structure, byId) => (match, index) => {
  const src = match.sources[index];
  if (!src?.match || src.take !== 'loser' || !byId[src.match]) return null;
  return `Loser of ${shortRound(structure, byId[src.match])}`;
};

const nameOf = (playersById, id, placeholderLabel) => {
  if (id === EMPTY) return 'Bye';
  if (!id) return 'TBD';
  if (playersById[id]?.name) return playersById[id].name;
  if (id.startsWith('seed:')) {
    const n = Number(id.slice(5));
    return placeholderLabel ? placeholderLabel(n) : `Seed ${n}`;
  }
  return 'Removed player';
};

const isVisible = (m) => m.status !== 'bye' && m.status !== 'skipped';
const visibleOf = (matches) => matches.filter(isVisible);

// First round with a ready match, else the last round with any visible match.
const defaultRound = (matches) => {
  const vis = visibleOf(matches);
  const ready = vis.find((m) => m.status === 'ready');
  if (ready) return ready.round;
  return vis.length ? vis[vis.length - 1].round : null;
};

const MatchCard = ({
  match,
  label,
  nameOf: getName,
  pendingName: getPending,
  seedOf,
  hovered,
  setHovered,
  onClick,
}) => {
  const clickable = !!onClick && (match.status === 'ready' || match.status === 'done');
  const Comp = clickable ? 'button' : 'div';
  const isGf2Pending = match.id === 'GF2' && match.status === 'pending';

  const row = (index) => {
    const id = match.players[index];
    const isTbdOrBye = id === EMPTY || !id;
    const isWinner = match.status === 'done' && id === match.winner;
    const isLoser = match.status === 'done' && id === match.loser;
    const isHovered = !isTbdOrBye && id === hovered;
    const seed = seedOf(id);
    const score = match.score[index];
    return (
      <div
        key={index}
        className={cn(
          'flex h-8 items-center gap-2 px-2',
          isWinner && 'border-l-2 border-l-winner bg-winner/5 font-medium text-foreground',
          isLoser && 'text-muted-foreground',
          isTbdOrBye && 'italic text-muted-foreground/70',
          !isWinner && isHovered && 'bg-heat/10'
        )}
        onMouseEnter={!isTbdOrBye ? () => setHovered(id) : undefined}
        onMouseLeave={!isTbdOrBye ? () => setHovered(null) : undefined}
      >
        <span className="w-5 text-right font-mono text-[10px] tabular-nums text-muted-foreground">
          {seed ?? ''}
        </span>
        <span className="min-w-0 flex-1 truncate">
          {(!id && getPending?.(match, index)) || getName(id)}
        </span>
        <span className={cn('w-6 text-right font-mono tabular-nums', isWinner && 'text-winner')}>
          {score ?? ''}
        </span>
      </div>
    );
  };

  const ariaLabel =
    `${label}: ${getName(match.players[0])} vs ${getName(match.players[1])}` +
    (match.status === 'done' ? `, ${getName(match.winner)} won` : '');

  return (
    <Comp
      type={clickable ? 'button' : undefined}
      onClick={clickable ? onClick : undefined}
      aria-label={ariaLabel}
      className={cn(
        'relative flex h-16 w-full flex-col overflow-hidden rounded-md border border-border/60 bg-card text-left text-sm shadow-sm',
        clickable &&
          'transition-colors hover:border-heat/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        clickable && match.status === 'ready' && 'border-heat/50',
        isGf2Pending && 'border-dashed'
      )}
    >
      {row(0)}
      <div className="h-px bg-border/60" />
      {row(1)}
    </Comp>
  );
};

const DesktopSide = ({
  matches,
  structure,
  byId,
  title,
  getName,
  seedOf,
  hovered,
  setHovered,
  onMatchClick,
}) => {
  const pos = useMemo(() => layoutSide(matches, structure), [matches, structure]);
  if (!matches.length) return null;
  const visibleMatches = visibleOf(matches);
  const maxX = Math.max(...matches.map((m) => pos[m.id].x));
  const maxY = Math.max(...matches.map((m) => pos[m.id].y));
  const columns = {};
  matches.forEach((m) => {
    const c = pos[m.id].column;
    if (!(c in columns)) columns[c] = m;
  });

  return (
    <div>
      {title && (
        <div className="mb-3 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
          <span className="h-px w-6 bg-heat" />
          {title}
        </div>
      )}
      <div className="overflow-x-auto pb-4">
        <div
          className="relative"
          style={{ width: maxX + MATCH_W, height: maxY + MATCH_H + HEAD_H }}
        >
          {Object.entries(columns).map(([c, m]) => (
            <div
              key={c}
              className="absolute top-0 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground"
              style={{ left: Number(c) * (MATCH_W + GAP_X) }}
            >
              {roundLabel(structure, m.side, m.round)}
            </div>
          ))}
          <svg
            aria-hidden
            className="pointer-events-none absolute left-0 text-border"
            style={{ top: HEAD_H, width: maxX + MATCH_W, height: maxY + MATCH_H }}
          >
            {visibleMatches.flatMap((m) =>
              m.sources
                .filter((s) => s.match && pos[s.match] && byId[s.match] && isVisible(byId[s.match]))
                .map((s, i) => {
                  const from = pos[s.match];
                  const to = pos[m.id];
                  const x1 = from.x + MATCH_W;
                  const y1 = from.y + MATCH_H / 2;
                  const x2 = to.x;
                  const y2 = to.y + MATCH_H / 2;
                  return (
                    <path
                      key={`${m.id}-${s.match}-${i}`}
                      d={`M ${x1} ${y1} H ${x1 + GAP_X / 2} V ${y2} H ${x2}`}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={1.5}
                    />
                  );
                })
            )}
          </svg>
          {visibleMatches.map((m) => (
            <div
              key={m.id}
              className="absolute"
              style={{
                left: pos[m.id].x,
                top: pos[m.id].y + HEAD_H,
                width: MATCH_W,
                height: MATCH_H,
              }}
            >
              <MatchCard
                match={m}
                label={`${roundLabel(structure, m.side, m.round)} · Match ${m.index}`}
                nameOf={getName}
                seedOf={seedOf}
                hovered={hovered}
                setHovered={setHovered}
                onClick={onMatchClick ? () => onMatchClick(m) : undefined}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const SECTION_H = 32;
const SECTION_GAP = 40;
const FINAL_LABEL_H = 22;

// Double elimination on one canvas: upper bracket on top, lower bracket below,
// and the finals in their own column to the right, level between the upper
// and lower final and wired to both.
const DesktopDouble = ({
  matches,
  structure,
  byId,
  getName,
  getPending,
  seedOf,
  hovered,
  setHovered,
  onMatchClick,
}) => {
  const layout = useMemo(() => {
    const upper = matches.filter((m) => m.side === 'W');
    const lower = matches.filter((m) => m.side === 'L');
    const finals = matches.filter((m) => m.side === 'GF');
    const posW = layoutSide(upper, structure);
    const posL = layoutSide(lower, structure);
    const upperH = Math.max(...upper.map((m) => posW[m.id].y)) + MATCH_H;
    const lowerTop = SECTION_H + HEAD_H + upperH + SECTION_GAP;
    const lowerH = lower.length ? Math.max(...lower.map((m) => posL[m.id].y)) + MATCH_H : 0;

    const pos = {};
    upper.forEach((m) => {
      pos[m.id] = { x: posW[m.id].x, y: SECTION_H + HEAD_H + posW[m.id].y };
    });
    lower.forEach((m) => {
      pos[m.id] = { x: posL[m.id].x, y: lowerTop + SECTION_H + HEAD_H + posL[m.id].y };
    });
    const finalsX = Math.max(structure.rounds, structure.lowerRounds) * (MATCH_W + GAP_X);
    const gf1 = finals.find((m) => m.id === 'GF1');
    const feeders = gf1 ? gf1.sources.filter((s) => s.match && pos[s.match]) : [];
    const gfY = feeders.length
      ? feeders.reduce((sum, s) => sum + pos[s.match].y, 0) / feeders.length
      : SECTION_H + HEAD_H;
    finals.forEach((m) => {
      pos[m.id] = { x: finalsX + (m.round - 1) * (MATCH_W + GAP_X), y: gfY };
    });

    const headers = [
      ...[...new Set(upper.map((m) => m.round))].map((r) => ({
        key: `W${r}`,
        text: roundLabel(structure, 'W', r),
        x: (r - 1) * (MATCH_W + GAP_X),
        y: SECTION_H,
      })),
      ...[...new Set(lower.map((m) => m.round))].map((r) => ({
        key: `L${r}`,
        text: roundLabel(structure, 'L', r),
        x: (r - 1) * (MATCH_W + GAP_X),
        y: lowerTop + SECTION_H,
      })),
      ...finals.map((m) => ({
        key: m.id,
        text: m.id === 'GF2' ? 'Reset · if needed' : 'Grand final',
        x: pos[m.id].x,
        y: gfY - FINAL_LABEL_H,
      })),
    ];

    const width = Math.max(...Object.values(pos).map((p) => p.x)) + MATCH_W;
    const height = Math.max(lowerTop + SECTION_H + HEAD_H + lowerH, gfY + MATCH_H);
    return { pos, headers, lowerTop, width, height, hasLower: lower.length > 0 };
  }, [matches, structure]);

  const { pos, headers, lowerTop, width, height, hasLower } = layout;
  const visibleMatches = visibleOf(matches);

  // One line per feeding match; losers dropping into the lower bracket are
  // named on the card instead of drawn, which would cross the whole canvas.
  const links = visibleMatches.flatMap((m) =>
    [...new Set(m.sources.filter((s) => s.match && s.take !== 'loser').map((s) => s.match))]
      .filter((id) => pos[id] && byId[id] && isVisible(byId[id]))
      .map((id) => {
        const x1 = pos[id].x + MATCH_W;
        const y1 = pos[id].y + MATCH_H / 2;
        const x2 = pos[m.id].x;
        const y2 = pos[m.id].y + MATCH_H / 2;
        return { key: `${m.id}-${id}`, d: `M ${x1} ${y1} H ${x2 - GAP_X / 2} V ${y2} H ${x2}` };
      })
  );

  const sectionTitle = (text, top) => (
    <div
      className="absolute left-0 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground"
      style={{ top }}
    >
      <span className="h-px w-6 bg-heat" />
      {text}
    </div>
  );

  return (
    <div className="overflow-x-auto pb-4">
      <div className="relative" style={{ width, height }}>
        {sectionTitle('Upper bracket', 0)}
        {hasLower && sectionTitle('Lower bracket', lowerTop)}
        {headers.map((h) => (
          <div
            key={h.key}
            className="absolute whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground"
            style={{ left: h.x, top: h.y }}
          >
            {h.text}
          </div>
        ))}
        <svg
          aria-hidden
          className="pointer-events-none absolute left-0 top-0 text-border"
          style={{ width, height }}
        >
          {links.map((l) => (
            <path key={l.key} d={l.d} fill="none" stroke="currentColor" strokeWidth={1.5} />
          ))}
        </svg>
        {visibleMatches.map((m) => (
          <div
            key={m.id}
            className="absolute"
            style={{ left: pos[m.id].x, top: pos[m.id].y, width: MATCH_W, height: MATCH_H }}
          >
            <MatchCard
              match={m}
              label={`${roundLabel(structure, m.side, m.round)} · Match ${m.index}`}
              nameOf={getName}
              pendingName={getPending}
              seedOf={seedOf}
              hovered={hovered}
              setHovered={setHovered}
              onClick={onMatchClick ? () => onMatchClick(m) : undefined}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const BracketView = ({
  stage,
  playersById = {},
  onMatchClick,
  placeholderCount = 0,
  placeholderLabel,
}) => {
  const participants = useMemo(
    () =>
      stage.participants && stage.participants.length
        ? stage.participants
        : Array.from({ length: placeholderCount }, (_, i) => `seed:${i + 1}`),
    [stage.participants, placeholderCount]
  );

  const structure = useMemo(
    () => bracketFor(stage, participants.length),
    [stage, participants.length]
  );
  const resolved = useMemo(
    () => resolveBracket(structure, participants, stage.results || {}),
    [structure, participants, stage.results]
  );

  const [hovered, setHovered] = useState(null);
  const getName = (id) => nameOf(playersById, id, placeholderLabel);
  const getPending = useMemo(
    () => pendingName(structure, resolved.byId),
    [structure, resolved.byId]
  );
  const seedOf = (id) => {
    const idx = participants.indexOf(id);
    return idx === -1 ? null : idx + 1;
  };

  const upperMatches = useMemo(
    () => resolved.matches.filter((m) => m.side === 'W' || m.side === 'GF'),
    [resolved]
  );
  const lowerMatches = useMemo(() => resolved.matches.filter((m) => m.side === 'L'), [resolved]);

  const sides = useMemo(() => {
    if (structure.elimination !== 'double') {
      return [{ key: 'W', label: 'Bracket', matches: upperMatches }];
    }
    return [
      { key: 'W', label: 'Upper', matches: upperMatches.filter((m) => m.side === 'W') },
      { key: 'L', label: 'Lower', matches: lowerMatches },
      { key: 'GF', label: 'Finals', matches: upperMatches.filter((m) => m.side === 'GF') },
    ];
  }, [structure.elimination, upperMatches, lowerMatches]);

  const [mobileSideKey, setMobileSideKey] = useState(sides[0].key);
  const activeSide = sides.find((s) => s.key === mobileSideKey) || sides[0];
  const [mobileRound, setMobileRound] = useState(() => defaultRound(activeSide.matches));

  const selectSide = (key) => {
    const side = sides.find((s) => s.key === key);
    setMobileSideKey(key);
    setMobileRound(defaultRound(side.matches));
  };

  if (participants.length < 2) {
    return (
      <div className="rounded-md border border-dashed border-border/60 px-6 py-12 text-center text-sm text-muted-foreground">
        The bracket appears once players are seeded.
      </div>
    );
  }

  const mobileVisible = visibleOf(activeSide.matches);
  const mobileRounds = [...new Set(mobileVisible.map((m) => m.round))].sort((a, b) => a - b);
  const effectiveRound = mobileRounds.includes(mobileRound)
    ? mobileRound
    : mobileRounds[mobileRounds.length - 1];
  const roundMatches = mobileVisible.filter((m) => m.round === effectiveRound);

  return (
    <div>
      <div className="hidden md:block">
        {structure.elimination === 'double' ? (
          <DesktopDouble
            matches={resolved.matches}
            structure={structure}
            byId={resolved.byId}
            getName={getName}
            getPending={getPending}
            seedOf={seedOf}
            hovered={hovered}
            setHovered={setHovered}
            onMatchClick={onMatchClick}
          />
        ) : (
          <DesktopSide
            matches={upperMatches}
            structure={structure}
            byId={resolved.byId}
            getName={getName}
            seedOf={seedOf}
            hovered={hovered}
            setHovered={setHovered}
            onMatchClick={onMatchClick}
          />
        )}
      </div>

      <div className="md:hidden">
        {sides.length > 1 && (
          <div
            role="tablist"
            className="mb-3 inline-flex rounded-md border border-border/60 bg-background/40 p-0.5"
          >
            {sides.map((s) => (
              <button
                key={s.key}
                type="button"
                role="tab"
                aria-selected={s.key === mobileSideKey}
                onClick={() => selectSide(s.key)}
                className={cn(
                  'px-4 py-1.5 font-mono text-xs font-medium uppercase tracking-[0.2em] transition-colors',
                  s.key === mobileSideKey
                    ? 'bg-secondary text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}

        <div className="mb-3 flex gap-2 overflow-x-auto pb-1">
          {mobileRounds.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setMobileRound(r)}
              className={cn(
                'shrink-0 whitespace-nowrap rounded-md border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors',
                r === effectiveRound
                  ? 'border-heat/60 bg-heat/5 text-foreground'
                  : 'border-border/60 text-muted-foreground hover:border-border'
              )}
            >
              {roundLabel(structure, activeSide.key, r)}
            </button>
          ))}
        </div>

        <ul className="space-y-3">
          {roundMatches.map((m) => (
            <li key={m.id}>
              <MatchCard
                match={m}
                label={`${roundLabel(structure, m.side, m.round)} · Match ${m.index}`}
                nameOf={getName}
                pendingName={getPending}
                seedOf={seedOf}
                hovered={hovered}
                setHovered={setHovered}
                onClick={onMatchClick ? () => onMatchClick(m) : undefined}
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default BracketView;
